// cardRender.js – fertige Memo-Karten (Fotokarte + Namenskarte) als Bild für den Druck
// Gleiche Masse wie MemoCard.jsx / MemoCard.css, aber auf ein Canvas gezeichnet.
import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { SymbolShape } from '../components/PersonCard';
import { splitName, cardDate, loadImage, renderToCanvas, defaultEdit } from './photoEdit';
import { ORNAMENT_PATH, ORNAMENT_BOX } from './memoOrnament';

// 6 × 6 cm bei 300 dpi
export const CARD_CM = 6;
export const CARD_DPI = 300;
export const CARD_PX = Math.round((CARD_CM / 2.54) * CARD_DPI); // 709

const GENDER_COLORS = { male: '#5A9ED6', female: '#D4787A' };
const FONT = "'MemoHand', 'Patrick Hand SC', 'Comic Sans MS', cursive";
const SKEW = Math.tan((-9 * Math.PI) / 180);

// Rahmen: Abschnitte in CSS-Grad (0° = oben, im Uhrzeigersinn) – wie frameBackground() in MemoCard.jsx
function frameSegments(person, colorInfo) {
  const cols = colorInfo?.borderColors;
  if (cols && cols.length) {
    const n = cols.length;
    if (n === 1) return [[0, 360, cols[0]]];
    if (n === 2) return [[0, 90, cols[1]], [90, 270, cols[0]], [270, 360, cols[1]]];
    if (n === 3) return [[0, 98, cols[1]], [98, 262, cols[0]], [262, 360, cols[2]]];
    const seg = 360 / n;
    const start = 180 - seg / 2;
    return cols.map((c, i) => [start + i * seg, start + (i + 1) * seg, c]);
  }
  return [[0, 360, colorInfo?.color || GENDER_COLORS[person.gender] || '#8B8B8B']];
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// Abstand von der Oberkante der Zeile (line-height 0.661) bis zur Grundlinie
function baselineOffset(ctx, fs) {
  const m = ctx.measureText('Hg');
  const asc = m.fontBoundingBoxAscent ?? fs * 0.9;
  const desc = m.fontBoundingBoxDescent ?? fs * 0.3;
  return (0.661 * fs - (asc + desc)) / 2 + asc;
}

// Text wie in der Vorlage: leicht schräg, mit Kontur
function drawHandText(ctx, text, fs, { x, top, align, originX, originY }) {
  ctx.save();
  ctx.font = `${fs}px ${FONT}`;
  ctx.textAlign = align;
  ctx.textBaseline = 'alphabetic';
  const base = top + baselineOffset(ctx, fs);
  ctx.translate(originX, originY);
  ctx.transform(1, 0, SKEW, 1, 0, 0);
  ctx.fillStyle = '#111';
  ctx.strokeStyle = '#111';
  ctx.lineWidth = 0.025 * fs;
  ctx.lineJoin = 'round';
  ctx.fillText(text, x - originX, base - originY);
  ctx.strokeText(text, x - originX, base - originY);
  ctx.restore();
}

const symbolCache = new Map();
function symbolImage(type, outline, color) {
  const key = `${type}|${outline}|${color}`;
  if (!symbolCache.has(key)) {
    const svg = renderToStaticMarkup(
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12" width="120" height="120">
        {outline
          ? <SymbolShape type={type} color="none" stroke={color} strokeWidth={1.4} size={12} />
          : <SymbolShape type={type} color={color} stroke="#333" strokeWidth={0.4} size={12} />}
      </svg>
    );
    symbolCache.set(key, loadImage(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`));
  }
  return symbolCache.get(key);
}

async function drawSymbols(ctx, colorInfo, W, inset, I) {
  const syms = [];
  if (colorInfo?.outlineSymbol) syms.push({ type: colorInfo.outlineSymbol, outline: true });
  (colorInfo?.symbols || []).forEach(t => syms.push({ type: t, outline: false }));
  if (!syms.length) return;
  const color = colorInfo?.color || '#555';
  const h = 0.028 * I;
  const gap = 0.017 * W;
  const right = inset + I - 0.047 * I;
  const bottom = inset + I - 0.012 * I;
  let x = right - syms.length * h - (syms.length - 1) * gap;
  for (const s of syms) {
    try {
      const img = await symbolImage(s.type, s.outline, color);
      ctx.drawImage(img, x, bottom - h, h, h);
    } catch { /* Symbol fehlt → weglassen */ }
    x += h + gap;
  }
}

// side: 'front' (Fotokarte) | 'back' (Namenskarte)
export async function renderCardCanvas(person, colorInfo, side, W = CARD_PX) {
  await document.fonts.load(`${Math.round(W * 0.14)}px MemoHand`, `${person.name || ''}${person.maidenName || ''}0123456789·()`).catch(() => {});
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = W;
  const ctx = canvas.getContext('2d');

  // farbiger Rahmen mit runden Ecken
  const segments = frameSegments(person, colorInfo);
  const paintFrame = () => {
    const c = W / 2, R = W;
    for (const [a0, a1, color] of segments) {
      ctx.beginPath();
      ctx.moveTo(c, c);
      ctx.arc(c, c, R, ((a0 - 90) * Math.PI) / 180, ((a1 - 90) * Math.PI) / 180);
      ctx.closePath();
      ctx.fillStyle = color;
      ctx.fill();
    }
  };
  ctx.save();
  roundRect(ctx, 0, 0, W, W, 0.083 * W);
  ctx.clip();
  paintFrame();
  ctx.restore();

  // weisse Fläche
  const inset = 0.094 * W;
  const I = W - 2 * inset;
  ctx.fillStyle = '#fff';
  ctx.fillRect(inset, inset, I, I);

  const { first, last } = splitName(person);
  if (side === 'front') {
    const px = inset + 0.0505 * I, py = inset + 0.0554 * I, ps = 0.895 * I;
    if (person.photo) {
      const img = await loadImage(person.photo);
      const edit = person.photoEdit && person.photoEdit.w === img.naturalWidth && person.photoEdit.h === img.naturalHeight
        ? person.photoEdit : defaultEdit(img.naturalWidth, img.naturalHeight);
      const photo = renderToCanvas(img, edit, Math.round(ps));
      ctx.drawImage(photo, px, py, ps, ps);
    } else {
      // kein Foto: Zierrahmen in den Farben des äusseren Rahmens
      const k = (ORNAMENT_BOX.size * W) / 1000;
      const orn = new Path2D();
      orn.addPath(new Path2D(ORNAMENT_PATH), new DOMMatrix([k, 0, 0, k, ORNAMENT_BOX.left * W, ORNAMENT_BOX.top * W]));
      ctx.save();
      ctx.clip(orn, 'evenodd');
      paintFrame();
      ctx.restore();
    }
    // Vorname unten links (Ursprung der Schrägstellung: links unten)
    const fs = 0.045 * W;
    const top = inset + 0.953 * I;
    const left = inset + 0.042 * I;
    drawHandText(ctx, first || '', fs, { x: left, top, align: 'left', originX: left, originY: top + 0.661 * fs });
  } else {
    const lines = [];
    lines.push({ text: first || '', fs: 0.139 * W, mt: 0 });
    if (last) lines.push({ text: last, fs: 0.139 * W, mt: 0.446 });
    if (person.maidenName) lines.push({ text: `(geb. ${person.maidenName})`, fs: 0.070 * W, mt: 0.47 });
    if (person.birthDate) lines.push({ text: cardDate(person.birthDate), fs: 0.077 * W, mt: person.maidenName ? 1.30 : 1.35 });
    lines[0].mt = 0; // erster Eintrag ohne Abstand nach oben
    const total = lines.reduce((s, l) => s + l.mt * l.fs + 0.661 * l.fs, 0);
    let y = inset + (I - total) / 2;
    const cx = inset + I / 2;
    for (const l of lines) {
      y += l.mt * l.fs;
      const h = 0.661 * l.fs;
      drawHandText(ctx, l.text, l.fs, { x: cx, top: y, align: 'center', originX: cx, originY: y + h / 2 });
      y += h;
    }
  }
  await drawSymbols(ctx, colorInfo, W, inset, I);
  return canvas;
}

export function canvasToBlob(canvas, type = 'image/png', quality) {
  return new Promise(resolve => canvas.toBlob(resolve, type, quality));
}
